import { isNil } from 'lodash';
import { ViewContentSourceEnum } from 'src/app/view/models/view-content-source.enum';

// get possible settings keys. see: https://stackoverflow.com/questions/49401866/all-possible-keys-of-an-union-type
type KeysOfUnion<T> = T extends any ? keyof T : never;

export interface AccountSettingValue {
  value: string;
  description?: string;
}

/// List of Account Settings
export interface AccountPurposeSetting {
  account_purpose: AccountSettingValue;
}

export interface AccountRoleSetting {
  account_role: AccountSettingValue;
}

export interface AccountFieldSetting {
  account_field: AccountSettingValue;
}

export interface AccountCompanySizeSetting {
  account_company_size: string;
}

export interface AccountCompanyWebsiteSetting {
  account_company_website: string;
}

export interface AccountDefaultRoleSetting {
  default_user_role_id: number;
}

export interface AccountAnatomyDescriptionFallbackSetting {
  anatomy_description_fallback: ViewContentSourceEnum;
}

// individual user settings
export type UserAccountSetting = AccountPurposeSetting | AccountRoleSetting | AccountFieldSetting;

// team settings
export type TeamAccountSetting =
  | AccountPurposeSetting
  | AccountFieldSetting
  | AccountCompanySizeSetting
  | AccountCompanyWebsiteSetting
  | AccountDefaultRoleSetting
  | AccountAnatomyDescriptionFallbackSetting;

export type AccountSetting = UserAccountSetting | TeamAccountSetting;

export type AccountSettingKeys = KeysOfUnion<AccountSetting>;
export type AccountSettingsMap = Record<AccountSettingKeys, AccountSettingValue>;

/// Utilities

// converts a list of account settings to a lookup
export function createAccountSettingsMap<T extends AccountSetting>(
  accountSettings: T[],
  trimEmpty = false,
): Partial<AccountSettingsMap> {
  const settings = trimEmpty ? trimEmptySettings(accountSettings) : accountSettings;

  return settings.reduce((acc, value) => {
    return { ...acc, ...value };
  }, {});
}

// converts a lookup of account settings to a list of settings
export function createAccountSettingsArray<T extends AccountSetting>(
  accountSettingsMap: Partial<AccountSettingsMap>,
  trimEmpty = false,
): T[] {
  const accountSettings = Object.entries(accountSettingsMap).map(([key, value]) => {
    return <T>(<unknown>{ [key]: value });
  });

  return trimEmpty ? trimEmptySettings(accountSettings) : accountSettings;
}

// trim settings with empty "value" fields (i.e., setting.account_purpose.value === '')
function trimEmptySettings<T extends AccountSetting>(accountSettings: T[]): T[] {
  return accountSettings.filter((setting: T) => {
    // OK: any non empty value
    return Object.values(setting).some(
      (fieldValue) => !isNil(fieldValue.value) && fieldValue.value !== '',
    );
  });
}
